<template>
  <div class="h-full">
    <Button type="button" icon-name="close" class="btn-icon btn-rounded btn-flat absolute right-4 top-4 z-20 size-12 active:text-icon-active" @click="showModal = false" />

    <div class="flex h-full flex-col gap-4 pt-8">
      <div ref="emblaMainRef" class="relative grow overflow-hidden">
        <div class="-ml-5 flex h-full" style="backface-visibility: hidden; touch-action: pan-y pinch-zoom">
          <ZoomContainer
            v-for="(item, index) of props.images"
            :key="index"
            class="flex h-full max-w-full shrink-0 basis-full pl-5"
            :active="currentSlideLightbox === index"
            @zoom-change="(zoomed) => (movementLocked = zoomed)"
          >
            <img class="m-auto max-h-full touch-none select-none object-contain" :src="item.url" :alt="item.alt" :width="item.width" :height="item.height" />
          </ZoomContainer>
        </div>

        <div class="pointer-events-none absolute inset-0 flex items-center justify-between px-4">
          <button
            class="btn-rounded btn-secondary pointer-events-auto flex size-12 touch-manipulation items-center justify-center transition-all"
            type="button"
            :aria-label="t['global.previous']()"
            :disabled="isFirstSlide"
            @click="handleArrowClick('prev')"
          >
            <Icon class="size-5" name="arrow-left" />
          </button>
          <button
            class="btn-rounded btn-secondary pointer-events-auto flex size-12 touch-manipulation items-center justify-center transition-all"
            type="button"
            :aria-label="t['global.next']()"
            :disabled="isLastSlide"
            @click="handleArrowClick('next')"
          >
            <Icon class="size-5" name="arrow-right" />
          </button>
        </div>
      </div>

      <div class="shrink-0 text-center text-sm">{{ currentSlideLightbox + 1 }} / {{ props.images.length }}</div>

      <div class="mx-auto max-w-full shrink-0 px-4 pb-4">
        <div ref="emblaThumbsRef" class="overflow-hidden">
          <div class="-ml-4 flex" style="backface-visibility: hidden; touch-action: pan-y pinch-zoom">
            <div v-for="(item, index) of props.images" :key="index" class="shrink-0 pl-4">
              <button
                class="flex h-[72px] w-24 items-center justify-center overflow-hidden rounded-2xl border-2 hover:border-border-hover xl:h-24 xl:w-32"
                :class="[currentSlideLightbox === index ? 'border-border' : 'border-transparent']"
                @click="handleThumbnailClick(index)"
              >
                <Icon v-if="item.isDimensionsImage" name="dimension-lightbox" class="h-24 w-32" />
                <img v-else class="h-full object-cover" :src="item.url" :alt="item.alt" :width="item.width" :height="item.height" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import emblaCarouselVue from 'embla-carousel-vue'
import { onKeyStroke } from '@vueuse/core'
import ZoomContainer from './ZoomContainer.vue'

import Icon from '~/components/icon/Icon.vue'
import Button from '~/components/button/Button.vue'
import * as t from '~/messages'

const showModal = defineModel<boolean>({ default: false })
const currentSlideLightbox = defineModel<number>('currentSlide', { default: 0 })

interface Image {
  url: string
  alt: string
  width: number
  height: number
  isDimensionsImage: boolean
  index: number
}

const props = withDefaults(
  defineProps<{
    images?: Image[]
  }>(),
  {
    images: () => [],
  }
)

const handleThumbnailClick = (index: number) => {
  emblaMainApi.value?.scrollTo(Math.max(0, Math.min(index, props.images.length - 1)))
}

watch(currentSlideLightbox, () => {
  emblaMainApi.value?.scrollTo(currentSlideLightbox.value)
  emblaThumbsApi.value?.scrollTo(currentSlideLightbox.value)
})

onKeyStroke('ArrowLeft', (e) => {
  e.preventDefault()
  handleArrowClick('prev')
})

onKeyStroke('ArrowRight', (e) => {
  e.preventDefault()
  handleArrowClick('next')
})

const handleArrowClick = (direction: 'prev' | 'next') => {
  switch (direction) {
    case 'prev':
      emblaMainApi.value?.scrollPrev()
      break
    case 'next':
      emblaMainApi.value?.scrollNext()
      break
  }
}

const movementLocked = ref(false)

const [emblaMainRef, emblaMainApi] = emblaCarouselVue({
  align: 'start',
  slidesToScroll: 1,
  startIndex: currentSlideLightbox.value,
  watchDrag: () => !movementLocked.value,
})

const [emblaThumbsRef, emblaThumbsApi] = emblaCarouselVue({
  containScroll: 'keepSnaps',
  dragFree: true,
  startIndex: currentSlideLightbox.value,
})

const isScrollable = ref(true)

const toggleActiveWhenScrollable = () => {
  isScrollable.value = (emblaThumbsApi.value?.internalEngine().scrollSnaps.length ?? 0) > 1
}

watch(isScrollable, () => {
  emblaThumbsApi.value?.reInit({ active: isScrollable.value })
})

const isFirstSlide = ref(true)
const isLastSlide = ref(true)

onMounted(() => {
  const updateSelectedIndex = () => {
    if (!emblaMainApi.value) {
      return
    }

    isFirstSlide.value = !emblaMainApi.value.canScrollPrev()
    isLastSlide.value = !emblaMainApi.value.canScrollNext()

    const newSlide = emblaMainApi.value.selectedScrollSnap()

    currentSlideLightbox.value = newSlide
    emblaThumbsApi.value?.scrollTo(newSlide)
  }

  if (emblaMainApi.value && emblaThumbsApi.value) {
    emblaMainApi.value.on('init', updateSelectedIndex).on('reInit', updateSelectedIndex).on('select', updateSelectedIndex)

    toggleActiveWhenScrollable()
  }
})

useResizeObserver(
  emblaThumbsRef,
  useThrottleFrame(() => {
    toggleActiveWhenScrollable()
  })
)
</script>
